import { object, string, mixed, array, boolean, ValidationError } from 'yup';
import { TFunction } from 'react-i18next';
import { Benefit } from 'all-in-one-shared-api';
import { getBenefitLocaleDescription } from './components/ClaimAmountCard';
import { FileUploaded } from '../../components/form';
import { isLocaleEnglish } from 'utils/languageUtils';
import { ReceiptItem } from 'all-in-one-shared-api/dist/apis/claims/interfaces';

export interface ClaimFormState {
  claimType?: string;
  claims?: Benefit[];
  patientMemberType: string;
  patientName?: string;
  // antd didn't export their file type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  receipts?: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  referralLetters?: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  otherDocuments?: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  prevClaimSettlements?: any[];
  currency: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  receiptAmounts: any[];
  doctorId?: string;
  doctorName?: string;
  doctorNameQuery?: string;
  diagnosisCode?: string;
  diagnosisName?: {
    code: string;
    chineseDescription: string;
    description: string;
  };
  doctorDetails?: {
    doctorCode: string;
    doctorName: string;
    doctorChineseName: string;
  };
  treatmentDate?: string;
  claimTypeObject?: string[];
  partialClaim?: boolean;
  isFirstClaim?: boolean;
  firstClaimWithGenerali?: boolean;
  anotherPolicyNumber?: string;
  anotherMemberId?: string;
  otherInsurerName?: string;
  firstClaimCurrency?: string;
  firstClaimAmount?: string;
  requiredDocs?: (string | undefined)[];
  ctcRequest?: boolean;
  editAllowed?: boolean;
  referenceNo?: string;
  receiptItems?: ReceiptItem[];
}
const patternTwoDigisAfterComma = /^\d{1,8}\.\d{0,2}$|^\d{1,8}$/;

export const excludeRemovedDocuments = (documents: FileUploaded[] | undefined) =>
  (documents || []).filter((document) => document.status !== 'removed');

export const hasNoDocuments = (documents: FileUploaded[] | undefined) => {
  const filteredDocuments = excludeRemovedDocuments(documents);
  return filteredDocuments && filteredDocuments.length === 0;
};

const fillUndefinedValues = (amount: number, arr?: unknown[]) => {
  if (!amount) {
    return arr || [];
  }
  if (arr) {
    while (arr.length < amount) {
      arr.push(undefined);
    }
    return arr;
  }
  return new Array(amount).fill(undefined);
};

export const getClaimOcrSchema = (t: TFunction, language: string) =>
  object({
    claimType: string(),
    claims: array(mixed()),
    patientMemberType: string().required(t('newClaim.start.selectPatient')),
    requiredDocs: array(mixed()),
    receipts: array(mixed()).test({
      name: 'receiptsTest',
      test: function (value, ctx) {
        if (hasNoDocuments(value)) {
          return ctx.createError({
            message: t('newClaim.supplementaryDocuments.uploadingReceiptInfo.receiptIsRequired'),
          });
        }
        return true;
      },
    }),
    referralLetters: array(mixed()).test({
      name: 'referralLettersTest',
      test: function (value, ctx) {
        // 在step=0时非必填
        if (this.options.context?.step === 0) {
          return true;
        }
        console.log('referralLettersTest', this.parent?.requiredDocs, value);
        if (this.parent?.requiredDocs && this.parent?.requiredDocs[1] && hasNoDocuments(value)) {
          if (hasNoDocuments(this.parent?.receipts)) {
            return ctx.createError({
              message: t('newClaim.supplementaryDocuments.uploadingReferralInfo.referralLetterIsRequired', {
                documentName: this.parent?.requiredDocs[1],
              }),
            });
          }
        }
        return true;
      },
    }),
    receiptAmounts: array(string()).test({
      name: 'receiptAmountTest',
      test: function (value, ctx) {
        // 在step=0时非必填
        if (this.options.context?.step === 0) {
          return true;
        }

        // 在step=1时进行必填校验
        if (!value) return false;

        const errors: ValidationError[] = [];

        const checkValue = fillUndefinedValues(this.parent.claims.length, value);

        checkValue.forEach((o, i) => {
          if (!o || !o.match(patternTwoDigisAfterComma) || parseFloat(o) === 0) {
            if (!this.parent?.claims || !this.parent?.claims[i]) {
              errors.push(new ValidationError(t('common.error.requiredField'), 'receiptAmounts.0', 'receiptAmounts.0'));
            } else {
              errors.push(
                new ValidationError(
                  t('newClaim.paymentInfo.benefitAmountIsRequired', {
                    benefit: getBenefitLocaleDescription(
                      isLocaleEnglish(language),
                      this.parent?.claims ? this.parent?.claims[i] : undefined,
                    ),
                  }),
                  `receiptAmounts.${i}`,
                  `receiptAmounts.${i}`,
                ),
              );
            }
          }
        });

        if (!checkValue?.length && !errors.length) {
          errors.push(new ValidationError(t('common.error.requiredField'), 'receiptAmounts.0', 'receiptAmounts.0'));
        }

        if (errors.length)
          return ctx.createError({
            message: () => errors,
          });

        return true;
      },
    }),
    currency: string().test({
      name: 'currencyTest',
      test: function (value, ctx) {
        // 在step=0时非必填，其他步骤时必填
        if (this.options.context?.step === 0) {
          return true;
        }

        if (!value) {
          return ctx.createError({
            message: t('newClaim.paymentInfo.currencyIsRequired'),
          });
        }
        return true;
      },
    }),
    otherDocuments: array(mixed()),
    doctorId: string(),
    doctorName: string(),
    doctorNameQuery: string(),
    diagnosisCode: string().test({
      name: 'diagnosisCodeTest',
      test: function (value, ctx) {
        // 在step=1时必填，step=0时非必填
        if (this.options.context?.step === 1 && !value) {
          return ctx.createError({
            message: t('newClaim.claimInfo.diagnosisIsRequired'),
          });
        }
        return true;
      },
    }),
    treatmentDate: string().test({
      name: 'treatmentDateTest',
      test: function (value, ctx) {
        // 在step=1时必填，step=0时非必填
        if (this.options.context?.step === 1 && !value) {
          return ctx.createError({
            message: t('newClaim.claimInfo.treatmentDateIsRequired'),
          });
        }
        return true;
      },
    }),
    claimTypeObject: array(string()).test({
      name: 'claimTypeObjecTest',
      test: function (value, ctx) {
        // 在step=1时必填，step=0时非必填
        if (this.options.context?.step === 0) {
          return true;
        }

        if (!value || value.length === 0) {
          return ctx.createError({
            message: () => t('newClaim.start.claimTypeRequired'),
          });
        }

        // 检查是否有重复值
        if (value && value.length > 1) {
          const uniqueValues = new Set(value);
          if (uniqueValues.size !== value.length) {
            return ctx.createError({
              message: () => t('newClaim.start.existBenefit'),
            });
          }
        }

        return true;
      },
    }),
    partialClaim: boolean(),
    isFirstClaim: boolean().test({
      name: 'isFirstClaimTest',
      test: function (value) {
        if (this.parent?.partialClaim && value === undefined) return false;
        return true;
      },
      message: t('newClaim.start.partialClaim.partialClaimTypeRadioIsRequired'),
    }),
    firstClaimWithGenerali: boolean().test({
      name: 'firstClaimWithGeneraliTest',
      test: function (value) {
        if (this.parent?.isFirstClaim === false && value === undefined) return false;
        return true;
      },
      message: t('newClaim.start.partialClaim.firstClaimTypeIsRequired'),
    }),
    anotherPolicyNumber: string()
      .max(8)
      .test({
        name: 'anotherPolicyNumberTest',
        test: function (value) {
          if ((this.parent?.isFirstClaim || this.parent?.firstClaimWithGenerali) && !value) return false;
          return true;
        },
        message: t('newClaim.partialClaim.firstClaim.anotherGeneraliPolicyNoIsRequired'),
      }),
    anotherMemberId: string()
      .max(12)
      .test({
        name: 'anotherMemberIdTest',
        test: function (value) {
          if ((this.parent?.isFirstClaim || this.parent?.firstClaimWithGenerali) && !value) return false;
          return true;
        },
        message: t('newClaim.partialClaim.firstClaim.anotherGeneraliMemberIdIsRequired'),
      }),
    otherInsurerName: string().test({
      name: 'otherInsurerNameTest',
      test: function (value) {
        if (this.parent?.firstClaimWithGenerali === false && !value) return false;
        return true;
      },
      message: t('newClaim.partialClaim.secondClaim.insurerNameIsRequired'),
    }),
    firstClaimCurrency: string().test({
      name: 'firstClaimCurrencyTest',
      test: function (value) {
        if (this.parent?.firstClaimWithGenerali === false && !value) return false;
        return true;
      },
      message: t('newClaim.partialClaim.secondClaim.claimCurrencyIsRequired'),
    }),
    firstClaimAmount: string().test({
      name: 'firstClaimAmountTest',
      test: function (value) {
        if (this.parent?.firstClaimWithGenerali === false && (!value || !value?.match(patternTwoDigisAfterComma)))
          return false;

        return true;
      },
      message: t('newClaim.partialClaim.secondClaim.claimAmountIsRequired'),
    }),
    prevClaimSettlements: array(mixed()).test({
      name: 'prevClaimSettlementsTest',
      test: function (value) {
        return !(this.parent?.firstClaimWithGenerali === false && hasNoDocuments(value));
      },
      message: t('newClaim.partialClaim.secondClaim.settlementAdviceTitleIsRequired'),
    }),
  }).required();

export const getClaimSchema = (t: TFunction, language: string) =>
  object({
    claimType: string(),
    claims: array(mixed()),
    patientMemberType: string().required(t('newClaim.start.selectPatient')),
    requiredDocs: array(mixed()),
    receipts: array(mixed()).test({
      name: 'receiptsTest',
      test: function (value, ctx) {
        if (hasNoDocuments(this.parent?.referralLetters) && hasNoDocuments(value)) {
          return ctx.createError({
            message: t('newClaim.supplementaryDocuments.uploadingReceiptInfo.receiptIsRequired'),
          });
        }
        return true;
      },
    }),
    referralLetters: array(mixed()).test({
      name: 'referralLettersTest',
      test: function (value, ctx) {
        if (this.parent?.requiredDocs && this.parent?.requiredDocs[1] && hasNoDocuments(value)) {
          if (hasNoDocuments(this.parent?.receipts)) {
            return ctx.createError({
              message: t('newClaim.supplementaryDocuments.uploadingReferralInfo.referralLetterIsRequired', {
                documentName: this.parent?.requiredDocs[1],
              }),
            });
          }
        }
        return true;
      },
    }),
    otherDocuments: array(mixed()),
    prevClaimSettlements: array(mixed()).test({
      name: 'prevClaimSettlementsTest',
      test: function (value) {
        return !(this.parent?.firstClaimWithGenerali === false && hasNoDocuments(value));
      },
      message: t('newClaim.partialClaim.secondClaim.settlementAdviceTitleIsRequired'),
    }),
    currency: string().required(t('newClaim.paymentInfo.currencyIsRequired')),
    receiptAmounts: array(string()).test({
      name: 'receiptAmountTest',
      test: function (value, ctx) {
        if (!value) return false;

        const errors: ValidationError[] = [];

        const checkValue = fillUndefinedValues(this.parent.claims.length, value);

        checkValue.forEach((o, i) => {
          if (!o || !o.match(patternTwoDigisAfterComma)) {
            if (!this.parent?.claims || !this.parent?.claims[i]) {
              errors.push(new ValidationError(t('common.error.requiredField'), 'receiptAmounts.0', 'receiptAmounts.0'));
            } else {
              errors.push(
                new ValidationError(
                  t('newClaim.paymentInfo.benefitAmountIsRequired', {
                    benefit: getBenefitLocaleDescription(
                      isLocaleEnglish(language),
                      this.parent?.claims ? this.parent?.claims[i] : undefined,
                    ),
                  }),
                  `receiptAmounts.${i}`,
                  `receiptAmounts.${i}`,
                ),
              );
            }
          }
        });

        if (!checkValue?.length && !errors.length) {
          errors.push(new ValidationError(t('common.error.requiredField'), 'receiptAmounts.0', 'receiptAmounts.0'));
        }

        if (errors.length)
          return ctx.createError({
            message: () => errors,
          });

        return true;
      },
    }),
    doctorId: string(),
    doctorName: string(),
    doctorNameQuery: string(),
    diagnosisCode: string().required(t('newClaim.claimInfo.diagnosisIsRequired')),
    treatmentDate: string().required(t('newClaim.claimInfo.treatmentDateIsRequired')),
    claimTypeObject: array(string()).test({
      name: 'claimTypeObjecTest',
      test: function (value, ctx) {
        if (!value || value.length === 0) {
          return ctx.createError({
            message: () => t('newClaim.start.claimTypeRequired'),
          });
        }
        return true;
      },
    }),
    partialClaim: boolean(),
    isFirstClaim: boolean().test({
      name: 'isFirstClaimTest',
      test: function (value) {
        if (this.parent?.partialClaim && value === undefined) return false;
        return true;
      },
      message: t('newClaim.start.partialClaim.partialClaimTypeRadioIsRequired'),
    }),
    firstClaimWithGenerali: boolean().test({
      name: 'firstClaimWithGeneraliTest',
      test: function (value) {
        if (this.parent?.isFirstClaim === false && value === undefined) return false;
        return true;
      },
      message: t('newClaim.start.partialClaim.firstClaimTypeIsRequired'),
    }),
    anotherPolicyNumber: string()
      .max(8)
      .test({
        name: 'anotherPolicyNumberTest',
        test: function (value) {
          if ((this.parent?.isFirstClaim || this.parent?.firstClaimWithGenerali) && !value) return false;
          return true;
        },
        message: t('newClaim.partialClaim.firstClaim.anotherGeneraliPolicyNoIsRequired'),
      }),
    anotherMemberId: string()
      .max(12)
      .test({
        name: 'anotherMemberIdTest',
        test: function (value) {
          if ((this.parent?.isFirstClaim || this.parent?.firstClaimWithGenerali) && !value) return false;
          return true;
        },
        message: t('newClaim.partialClaim.firstClaim.anotherGeneraliMemberIdIsRequired'),
      }),
    otherInsurerName: string().test({
      name: 'otherInsurerNameTest',
      test: function (value) {
        if (this.parent?.firstClaimWithGenerali === false && !value) return false;
        return true;
      },
      message: t('newClaim.partialClaim.secondClaim.insurerNameIsRequired'),
    }),
    firstClaimCurrency: string().test({
      name: 'firstClaimCurrencyTest',
      test: function (value) {
        if (this.parent?.firstClaimWithGenerali === false && !value) return false;
        return true;
      },
      message: t('newClaim.partialClaim.secondClaim.claimCurrencyIsRequired'),
    }),
    firstClaimAmount: string().test({
      name: 'firstClaimAmountTest',
      test: function (value) {
        if (this.parent?.firstClaimWithGenerali === false && (!value || !value?.match(patternTwoDigisAfterComma)))
          return false;

        return true;
      },
      message: t('newClaim.partialClaim.secondClaim.claimAmountIsRequired'),
    }),
  }).required();
